export const CURRENCY_OPTIONS = [
  { code: "INR", label: "INR (₹)", symbol: "₹" },
  { code: "USD", label: "USD ($)", symbol: "$" },
  { code: "EUR", label: "EUR (€)", symbol: "€" },
  { code: "GBP", label: "GBP (£)", symbol: "£" },
  { code: "AED", label: "AED", symbol: "AED" },
];

export const getCurrencySymbol = (currency) => {
  const code = String(currency || "INR").toUpperCase();
  const option = CURRENCY_OPTIONS.find((item) => item.code === code);
  return option?.symbol || code;
};

/**
 * Build sequential room labels from a starting value.
 * - "101" + 3 → 101, 102, 103
 * - "Lotus" / "Lotus 1" + 3 → Lotus 1, Lotus 2, Lotus 3
 * - "Jasmine 3" + 3 → Jasmine 3, Jasmine 4, Jasmine 5
 */
export const generateRoomNumbersFromStart = (start, quantity) => {
  const q = parseInt(String(quantity).trim(), 10);
  if (Number.isNaN(q) || q < 1) return [];
  const raw = String(start ?? "").trim();
  if (!raw) return [];

  if (/^\d+$/.test(raw)) {
    const s = parseInt(raw, 10);
    return Array.from({ length: q }, (_, i) => String(s + i));
  }

  const trailingNumber = raw.match(/^(.*?)(\d+)$/);
  if (trailingNumber) {
    let prefix = trailingNumber[1];
    const startNum = parseInt(trailingNumber[2], 10);
    if (prefix && !/\s$/.test(prefix) && /[A-Za-z]$/.test(prefix)) {
      prefix = `${prefix} `;
    }
    return Array.from({ length: q }, (_, i) => `${prefix}${startNum + i}`);
  }

  return Array.from({ length: q }, (_, i) => `${raw} ${i + 1}`);
};

export const formatRoomPrice = (currency, price) => {
  if (price === "" || price == null || Number.isNaN(Number(price))) return "";
  const symbol = getCurrencySymbol(currency);
  const amount = Number(price);
  const formatted = Number.isInteger(amount)
    ? amount.toLocaleString("en-IN")
    : amount.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
  const gap = symbol.length > 1 && !symbol.endsWith(" ") ? " " : "";
  return `${symbol}${gap}${formatted}`;
};

export const normalizeNights = (nights) => {
  const value = Number(nights);
  if (!Number.isFinite(value) || value < 1) return 1;
  return Math.floor(value);
};

/** Stay total = per-night price × nights (nights defaults to 1). */
export const formatStayPrice = (currency, price, nights = 1) => {
  if (price === "" || price == null || Number.isNaN(Number(price))) return "";
  const nightCount = normalizeNights(nights);
  return formatRoomPrice(currency, Number(price) * nightCount);
};

/**
 * Hotel stay pricing: sum(room price × nights) grouped by currency.
 */
export const computeHotelPricing = (rooms = [], nights = 1) => {
  const nightCount = normalizeNights(nights);
  const list = Array.isArray(rooms) ? rooms : [];
  const byCurrency = {};
  let pricedRooms = 0;

  list.forEach((room) => {
    const raw = room?.price;
    if (raw === "" || raw == null || Number.isNaN(Number(raw))) return;
    const amount = Number(raw);
    if (amount < 0) return;
    const currency = String(room?.currency || "INR").trim().toUpperCase() || "INR";
    byCurrency[currency] = (byCurrency[currency] || 0) + amount * nightCount;
    pricedRooms += 1;
  });

  const totals = Object.keys(byCurrency)
    .sort()
    .map((currency) => ({
      currency,
      amount: byCurrency[currency],
      label: formatRoomPrice(currency, byCurrency[currency]),
    }));

  return {
    nights: nightCount,
    roomCount: list.length,
    pricedRooms,
    totals,
    label: totals.map((item) => item.label).filter(Boolean).join(" + "),
    breakdown:
      list.length > 0
        ? `${list.length} room${list.length === 1 ? "" : "s"} × ${nightCount} night${
            nightCount === 1 ? "" : "s"
          }`
        : "",
  };
};

export const formatHotelPricingSummary = (rooms = [], nights = 1) =>
  computeHotelPricing(rooms, nights).label || "";

export const computeEventHotelPricing = (hotels = []) => {
  const byCurrency = {};
  let roomCount = 0;
  let pricedRooms = 0;

  (hotels || []).forEach((hotel) => {
    const nights = normalizeNights(hotel?.totalNights);
    (hotel?.rooms || []).forEach((room) => {
      roomCount += 1;
      const raw = room?.price;
      if (raw === "" || raw == null || Number.isNaN(Number(raw))) return;
      const amount = Number(raw);
      if (amount < 0) return;
      const currency =
        String(room?.currency || "INR").trim().toUpperCase() || "INR";
      byCurrency[currency] = (byCurrency[currency] || 0) + amount * nights;
      pricedRooms += 1;
    });
  });

  const totals = Object.keys(byCurrency)
    .sort()
    .map((currency) => ({
      currency,
      amount: byCurrency[currency],
      label: formatRoomPrice(currency, byCurrency[currency]),
    }));

  return {
    roomCount,
    pricedRooms,
    totals,
    label: totals.map((item) => item.label).filter(Boolean).join(" + "),
  };
};

export const getBlockCategoryBaseTitle = (roomType, bedType) => {
  const type = String(roomType || "").trim();
  const bed = String(bedType || "").trim();
  const bedLabel = bed ? `${bed} Bed` : "";
  if (!type) return bedLabel ? `Rooms (${bedLabel})` : "Rooms";
  const isSuite = type.toLowerCase() === "suite";
  if (isSuite) return bedLabel ? `Suite (${bedLabel})` : "Suite";
  return bedLabel ? `${type} Room (${bedLabel})` : `${type} Room`;
};

export const getBlockCategoryTitle = (roomType, bedType, currency, price) => {
  const base = getBlockCategoryBaseTitle(roomType, bedType);
  const priceLabel = formatRoomPrice(currency, price);
  return priceLabel ? `${base} · ${priceLabel}` : base;
};

export const getBlockGroupKey = (roomType, bedType, currency = "", price = "") =>
  `${roomType || ""}|${bedType || ""}|${currency || ""}|${price ?? ""}`;

/* Room-type accents fixed by type (teal → purple) */
const ROOM_TYPE_ACCENT = {
  Standard: 1, // teal
  Deluxe: 2, // blue
  Premium: 3, // green
  Executive: 4, // orange
  Suite: 5, // purple
};

export const getCategoryAccentClass = (roomType, bedType = "") => {
  const type = String(roomType || "").trim();
  if (ROOM_TYPE_ACCENT[type]) return `accent-${ROOM_TYPE_ACCENT[type]}`;
  const seed = `${type}|${bedType || ""}`;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash + seed.charCodeAt(i)) % 5;
  }
  return `accent-${hash + 1}`;
};

export const buildHotelRoomGroups = (rooms = []) => {
  const groups = new Map();

  (rooms || []).forEach((room) => {
    const roomType = room.roomType || "";
    const bedType = room.bedType || room.bedSize || "";
    const currency = room.currency || "INR";
    const price = room.price ?? "";
    const key = getBlockGroupKey(roomType, bedType, currency, price);

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        roomType,
        bedType,
        currency,
        price,
        baseTitle: getBlockCategoryBaseTitle(roomType, bedType),
        priceLabel: formatRoomPrice(currency, price),
        title: getBlockCategoryTitle(roomType, bedType, currency, price),
        items: [],
      });
    }

    groups.get(key).items.push({
      roomNumber: room.roomNumber || "—",
      status: room.status === "occupied" ? "occupied" : "available",
      bedType,
      currency,
      price,
      guestName: room.guestName || room.guest || "",
    });
  });

  return Array.from(groups.values()).map((group) => ({
    ...group,
    items: [...group.items].sort((a, b) =>
      String(a.roomNumber).localeCompare(String(b.roomNumber), undefined, {
        numeric: true,
      })
    ),
    occupiedCount: group.items.filter((r) => r.status === "occupied").length,
    availableCount: group.items.filter((r) => r.status !== "occupied").length,
  }));
};

export const computeHotelMetrics = (rooms = []) => {
  const totalRooms = rooms.length;
  const occupied = rooms.filter((r) => r.status === "occupied").length;
  return {
    totalRooms,
    occupied,
    available: Math.max(totalRooms - occupied, 0),
  };
};

export const computeEventHotelMetrics = (hotels = []) => {
  let totalRooms = 0;
  let occupied = 0;

  (hotels || []).forEach((hotel) => {
    const rooms = hotel.rooms || [];
    if (hotel.metrics?.totalRooms != null) {
      totalRooms += hotel.metrics.totalRooms;
      occupied += hotel.metrics.occupied ?? 0;
    } else {
      const m = computeHotelMetrics(rooms);
      totalRooms += m.totalRooms;
      occupied += m.occupied;
    }
  });

  return {
    hotels: hotels.length,
    totalRooms,
    occupied,
    available: Math.max(totalRooms - occupied, 0),
  };
};

export const formatHotelAddress = (address = {}) =>
  [address.line1, address.city, address.state, address.pincode, address.country]
    .filter(Boolean)
    .join(", ");

export const GENERAL_ROOM_TYPE = "General";

const ROOM_TYPE_ORDER = [
  "Standard",
  "Deluxe",
  "Premium",
  "Executive",
  "Suite",
  GENERAL_ROOM_TYPE,
];

export const getHotelRoomTypeOptions = (rooms = []) => {
  const list = Array.isArray(rooms) ? rooms : [];
  const typed = [
    ...new Set(
      list.map((room) => String(room?.roomType || "").trim()).filter(Boolean)
    ),
  ];

  if (typed.length) {
    return typed.sort(
      (a, b) =>
        ROOM_TYPE_ORDER.indexOf(a) - ROOM_TYPE_ORDER.indexOf(b) ||
        a.localeCompare(b)
    );
  }

  return list.length ? [GENERAL_ROOM_TYPE] : [];
};

export const roomMatchesSelectedType = (room, selectedType) => {
  const roomType = String(room?.roomType || "").trim();
  if (!selectedType) return false;
  if (selectedType === GENERAL_ROOM_TYPE) return !roomType;
  return roomType === selectedType;
};
